import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import {
  EMPTY,
  Observable,
  of,
  ReplaySubject,
  Subject,
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  startWith,
  tap,
} from 'rxjs';

import { ProfileType } from '@fcom/core-api/login';
import { isDeepEqual, MonitoringLogger, WindowRef, toLookup } from '@fcom/core';
import { CookieService } from '@fcom/core/services/cookie/cookie.service';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { replaceURIFormat } from '@fcom/core/utils';
import { LanguageService } from '@fcom/ui-translate';
import { finShare, retryWithBackoff } from '@fcom/rx';

import {
  Consent,
  ConsentData,
  ConsentGroup,
  ConsentLayoutGroup,
  ConsentStatus,
  ConsentTextId,
  ConsentTextsGroup,
} from '../../interfaces';
import { GtmService, MarketingConsentTrackingService } from '../../gtm';

export const FINNAIR_COOKIE_SNIPPET = 'finnairCookieSnippet';

/**
 * First-party cookie the Finnair cookie snippet writes the customer's cookie-consent choices into.
 * Value is base64-encoded JSON: an array of `{ consentTextId, effectiveFrom, consentStatus }`. We
 * read it synchronously so consent-gated features (e.g. Datadog RUM) get an immediate answer instead
 * of waiting for the async snippet to load.
 */
export const FINNAIR_COOKIE_CONSENTS = 'FINNAIR_COOKIE_CONSENTS';

export enum ConsentTokenType {
  CAT = 'CAT',
  Bearer = 'Bearer',
}

export interface ConsentToken {
  token: string;
  type?: ConsentTokenType;
}

export enum CookieSnippetLoadStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

@Injectable()
export class ConsentService {
  private document = inject<Document>(DOCUMENT);
  private http = inject(HttpClient);
  private cookieService = inject(CookieService);
  private configService = inject(ConfigService);
  private gtmService = inject(GtmService);
  private monitoringLogger = inject(MonitoringLogger);
  private languageService = inject(LanguageService);
  private windowRef = inject(WindowRef);
  private marketingConsentTracking = inject(MarketingConsentTrackingService);

  cookieConsents$: Observable<ConsentGroup>;

  private readonly cookieConsentsSavedToStorage$ = new ReplaySubject<boolean>(1);
  private cookieSnippetLoaded$ = new Subject<CookieSnippetLoadStatus>();
  private MARKETING_CONSENT = 'fplus-marketing';

  constructor() {
    this.cookieConsents$ = this.cookieSnippetLoaded$.pipe(
      switchMap((status) => {
        if (status === CookieSnippetLoadStatus.ERROR) {
          return EMPTY;
        }
        return this.cookieConsentsSavedToStorage$.pipe(
          startWith(true),
          switchMap(() => this.getCookieConsents())
        );
      }),
      distinctUntilChanged(isDeepEqual),
      finShare()
    );
  }

  private getAuthHeaderValue(token: ConsentToken) {
    return token?.type === ConsentTokenType.CAT ? `CAT ${token.token}` : `Bearer ${token.token}`;
  }

  getMarketingConsents(
    token: ConsentToken,
    type: ProfileType,
    updateAnalyticsCookie = true
  ): Observable<ConsentGroup | undefined> {
    if (type === ProfileType.FINNAIR_ACCOUNT) {
      return of(undefined);
    }

    const headers = new HttpHeaders().append('Authorization', this.getAuthHeaderValue(token));
    return this.languageService.locale.pipe(
      switchMap((locale: string) =>
        this.http
          .get(
            `${this.configService.cfg.consentApiUrl}/v2/consents?locale=${locale}&consentGroup=${this.MARKETING_CONSENT}`,
            {
              headers,
            }
          )
          .pipe(
            retryWithBackoff(1),
            map((consentGroups: ConsentGroup[]) =>
              consentGroups.find((group) => group.consentGroup === this.MARKETING_CONSENT)
            ),
            tap((consentGroup: ConsentGroup | undefined) => {
              if (updateAnalyticsCookie) {
                this.marketingConsentTracking.writeConsentCookie(consentGroup?.consents);
              }
            }),
            catchError((e: unknown) => {
              this.handleError(e);
              throw e; // Throw the error after handling it
            })
          )
      )
    );
  }

  getMarketingConsentTexts(): Observable<ConsentTextsGroup> {
    return this.languageService.locale.pipe(
      switchMap((locale: string) =>
        this.http
          .get(
            `${this.configService.cfg.consentApiUrl}/v1/texts?locale=${locale}&consentGroup=${this.MARKETING_CONSENT}`
          )
          .pipe(
            retryWithBackoff(1),
            map((consentTextsGroups: ConsentTextsGroup[]) => consentTextsGroups[0]),
            catchError((e: unknown) => this.handleError(e))
          )
      )
    );
  }

  sendConsents(token: ConsentToken, consents: Consent[], source?: string): Observable<ConsentGroup | object> {
    const headers = new HttpHeaders().append('Authorization', this.getAuthHeaderValue(token));

    return this.http
      .post(
        `${this.configService.cfg.consentApiUrl}/v1/consents`,
        {
          consents,
          locale: this.languageService.localeValue,
          source: source || this.document.location.hostname,
        },
        { headers }
      )
      .pipe(catchError((e: unknown) => this.handlePostError(e)));
  }

  setCookieConsents(consentData: ConsentData): void {
    try {
      this.windowRef.nativeWindow[FINNAIR_COOKIE_SNIPPET]?.setConsents(consentData);
    } catch (e) {
      this.monitoringLogger.warn('Setting consents failed!', e);
    }

    this.gtmService.setConsents(consentData);
    this.cookieConsentsSavedToStorage$.next(true);
  }

  /**
   * Emits the customer's decision for `consentId` as a tri-state:
   * - `true`  — category ACCEPTED
   * - `false` — category DECLINED
   * - `null`  — not decided yet (WITHDRAWN/UNKNOWN/absent; the consent popup is/will be shown)
   *
   * The stream is seeded synchronously from the consent cookie so subscribers get an immediate answer
   * before the async snippet loads; the snippet's authoritative value follows and `distinctUntilChanged`
   * drops the duplicate when the two agree. Consumers that must gate on a real choice (and not act while
   * the decision is still pending) should check for an explicit boolean rather than truthiness.
   */
  getCookieConsentStatusById(consentId: ConsentTextId): Observable<boolean | null> {
    const status$ = this.cookieConsents$.pipe(
      map((cg: ConsentGroup) =>
        this.toConsentDecision(cg?.consents?.find((c) => c.consentTextId === consentId)?.consentStatus)
      )
    );

    return status$.pipe(startWith(this.readStoredConsentStatus(consentId)), distinctUntilChanged());
  }

  /**
   * Maps a raw consent status to the tri-state used by {@link getCookieConsentStatusById}: `true` for
   * ACCEPTED, `false` for DECLINED, and `null` for anything else (WITHDRAWN/UNKNOWN/absent), which
   * represents the "not decided yet" state.
   */
  private toConsentDecision(consentStatus?: ConsentStatus): boolean | null {
    if (consentStatus === ConsentStatus.ACCEPTED) {
      return true;
    }
    if (consentStatus === ConsentStatus.DECLINED) {
      return false;
    }
    return null;
  }

  /**
   * Synchronously reads `consentId`'s decision from the {@link FINNAIR_COOKIE_CONSENTS} cookie to seed
   * an immediate answer: `true` when ACCEPTED, `false` when DECLINED, and `null` when not decided yet
   * (on the server, when the cookie is missing, the category is absent/WITHDRAWN, or the cookie can't
   * be parsed).
   */
  private readStoredConsentStatus(consentId: ConsentTextId): boolean | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const raw = this.cookieService.getItem(FINNAIR_COOKIE_CONSENTS);
    if (!raw) {
      return null;
    }
    try {
      const consents = JSON.parse(atob(raw)) as Consent[];
      return this.toConsentDecision(consents.find((c) => c.consentTextId === consentId)?.consentStatus);
    } catch {
      return null;
    }
  }

  getCookieIdFromSnippet(): string {
    return this.windowRef.nativeWindow[FINNAIR_COOKIE_SNIPPET]?.getOrGenerateFinnairCookieId();
  }

  injectCookieSnippet(): void {
    const cookieSnippetScript: HTMLScriptElement = this.document.createElement('script');
    cookieSnippetScript.setAttribute('async', '');
    cookieSnippetScript.src = replaceURIFormat(this.configService.cfg.cookieSnippetUrl, {
      locale: this.languageService.localeValue,
    });
    cookieSnippetScript.onload = () => {
      this.cookieSnippetLoaded$.next(CookieSnippetLoadStatus.SUCCESS);
    };
    cookieSnippetScript.onerror = () => {
      this.cookieSnippetLoaded$.next(CookieSnippetLoadStatus.ERROR);
    };
    this.document.body.appendChild(cookieSnippetScript);
  }

  getMarketingConsentLayoutGroups(consents: Consent[]): ConsentLayoutGroup[] {
    const consentsById = toLookup(consents, (consent) => consent.consentTextId);
    return [
      {
        titleLabel: 'marketingConsent.optionalEmailsTitle',
        descriptionLabel: 'marketingConsent.optionalEmailsDescription',
        consentLayoutGroups: [
          {
            titleLabel: 'marketingConsent.finnairPlusTitle',
            consents: [consentsById[ConsentTextId.FPLUS_COMMUNICATION]].filter(Boolean) as Consent[],
          },
          {
            titleLabel: 'marketingConsent.remindersTipsAndOffersTitle',
            consents: [
              consentsById[ConsentTextId.BOOKING_COMMUNICATION],
              consentsById[ConsentTextId.FLIGHT_OFFERS],
            ].filter(Boolean) as Consent[],
          },
          {
            titleLabel: 'marketingConsent.eventsAndPartnerOffersTitle',
            consents: [
              consentsById[ConsentTextId.MEMBER_OFFERS_EVENT],
              consentsById[ConsentTextId.MEMBER_OFFERS_TRAVEL],
              consentsById[ConsentTextId.MEMBER_OFFERS_FINANCIAL],
              consentsById[ConsentTextId.MEMBER_OFFERS_RETAIL],
            ].filter(Boolean) as Consent[],
          },
        ],
      },
      {
        titleLabel: 'marketingConsent.personalisedContentAndAdsTitle',
        descriptionLabel: 'marketingConsent.personalisedContentAndAdsDescription',
        consents: [consentsById[ConsentTextId.PROFILING], consentsById[ConsentTextId.TARGETED_ADVERTISING]].filter(
          Boolean
        ) as Consent[],
      },
    ];
  }

  private getCookieConsents(): Observable<ConsentGroup> {
    return new Observable<ConsentGroup>((observer) => {
      try {
        this.windowRef.nativeWindow[FINNAIR_COOKIE_SNIPPET]?.getConsents((consentGroups) => {
          observer.next(consentGroups);
          observer.complete();
        });
      } catch (e) {
        observer.error(e);
      }
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return function dispose() {};
    });
  }

  private handleError(error: unknown): Observable<never> {
    if (error instanceof HttpErrorResponse && error.status !== 403) {
      this.monitoringLogger.error('Error getting marketing consent data', { error });
    }
    return EMPTY;
  }

  private handlePostError(error: unknown): Observable<ConsentGroup> {
    if (error instanceof HttpErrorResponse && error.status !== 403) {
      this.monitoringLogger.error('Error sending marketing consent data to consent api', { error });
    }
    return EMPTY;
  }
}
